import { useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { IDENTITY_REQUEST_TIMEOUT_SECONDS } from 'features/auth/constants';
import { isNoSessionRoute } from 'features/auth/utils/appRenderRule';
import { signinRedirectTarget } from 'features/auth/utils/authTarget';
import { maxAgeSinceStale } from 'features/auth/utils/lastActivity';
import { navigatorOutcome } from 'features/auth/utils/navigatorOutcome';
import { preconnectSignInHosts } from 'features/auth/utils/signInPreconnect';
import { resolveLoginBrandId } from 'features/brand/loginBrand';
import { logger } from 'features/logger/logger';
import {
  clearIntentionalNavigation,
  markIntentionalNavigation,
} from 'shared/utils/intentionalNavigationGuard';

const SSO_CHECK_TIMEOUT_MS = 5000; // 5 seconds timeout for SSO check

/**
 * Runs the startup Identity check once per page load and reports
 * `ssoCheckComplete` when the provider's login redirect may take over.
 *
 * - `/auth/callback` and `/signed-out`: no check; `ssoCheckComplete` is set at
 *   once.
 * - `staleAtLoad`: removes the stored user, then sets `ssoCheckComplete`; the
 *   provider's login redirect asks Identity for a sign-in made after the
 *   session ended. When the removal fails, the stored user keeps Identity
 *   authenticated, so the hook sends that login redirect itself and reports
 *   `forcedLoginFailed` when it fails.
 * - Otherwise: adds the sign-in connection hints, then `signinSilent()` in a
 *   hidden iframe signs in a participant who has an Identity session from
 *   another app. `ssoCheckComplete` is set when it settles or after 5 seconds,
 *   whichever comes first.
 */
export const useAutoSSO = (staleAtLoad: boolean) => {
  const auth = useAuth();
  const hasAttempted = useRef(false);
  const [ssoCheckComplete, setSsoCheckComplete] = useState(false);
  const [forcedLoginFailed, setForcedLoginFailed] = useState(false);

  useEffect(() => {
    // The check runs once per page load: a session that ends later on this
    // page (a logout, then a back/forward-cache restore) gets no silent
    // sign-in.
    if (hasAttempted.current) return;

    // No check on the callback route, where signinSilent races signinCallback,
    // or on the signed-out page, which must not sign a just-signed-out
    // participant back in.
    if (isNoSessionRoute(window.location.pathname)) {
      hasAttempted.current = true;
      setSsoCheckComplete(true);
      return;
    }

    // Don't attempt while loading or navigating
    if (auth.isLoading || auth.activeNavigator) {
      return;
    }

    // A session left idle past the limit (`staleAtLoad`) must not sign in with
    // the tokens the browser still holds or an Identity session from before
    // it ended: the stored user is removed and the provider's login redirect
    // asks Identity for a sign-in made after that moment. The key stays in
    // place, so the next visit asks for such a sign-in again; the next
    // completed sign-in overwrites it.
    if (staleAtLoad) {
      hasAttempted.current = true;

      void auth.removeUser().then(
        () => {
          setSsoCheckComplete(true);
        },
        (error: unknown) => {
          logger.error(
            'Could not remove the stored user before the forced login',
            undefined,
            error as Error
          );
          markIntentionalNavigation();
          const target = signinRedirectTarget();
          void resolveLoginBrandId(window.location.pathname)
            .then(brandId =>
              navigatorOutcome(
                auth.signinRedirect({
                  ...target,
                  max_age: maxAgeSinceStale(),
                  ...(brandId && { extraQueryParams: { brand: brandId } }),
                })
              )
            )
            .then(outcome => {
              clearIntentionalNavigation();

              if (outcome === 'failed') {
                setForcedLoginFailed(true);
                return;
              }

              logger.warn('Forced login redirect returned to the page', {
                details: { outcome },
              });
            });
        }
      );

      return;
    }

    // A page that loads with a session needs no check
    if (auth.isAuthenticated) {
      hasAttempted.current = true;
      return;
    }

    hasAttempted.current = true;

    logger.debug('Attempting silent SSO authentication');

    // Create timeout promise to prevent hanging indefinitely
    const timeoutPromise = new Promise<void>((_, reject) => {
      setTimeout(() => {
        reject(new Error('SSO check timeout'));
      }, SSO_CHECK_TIMEOUT_MS);
    });

    preconnectSignInHosts();

    // Race between SSO check and timeout
    Promise.race([
      auth.signinSilent({
        silentRequestTimeoutInSeconds: IDENTITY_REQUEST_TIMEOUT_SECONDS,
      }),
      timeoutPromise,
    ])
      .then(user => {
        // signinSilent() may resolve with void/undefined on error instead of rejecting
        // Only consider it successful if we actually got a user
        if (user) {
          logger.debug(
            'SSO authentication successful - user logged in from another domain'
          );
        } else {
          logger.debug('SSO check completed but no user returned');
        }
      })
      .catch((error: unknown) => {
        // Expected error if no SSO session exists OR if timeout occurs
        // This is normal - user can login manually
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        logger.debug('No active SSO session found', {
          details: { error: errorMessage },
        });
      })
      .finally(() => {
        setSsoCheckComplete(true);
      });
  }, [
    auth,
    auth.isAuthenticated,
    auth.isLoading,
    auth.activeNavigator,
    staleAtLoad,
  ]);

  return { ssoCheckComplete, forcedLoginFailed };
};
